import type { HTMLAttributes, ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "./cn";
import { EmptyState } from "./EmptyState";
import { Spinner } from "./Spinner";

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  onRowClick?: (row: T) => void;
  /**
   * Extra attributes for one row's <tr>, such as making it draggable.
   *
   * <p>A function of the row rather than a flag, because what a row carries when dragged is the
   * row's own business and this component has no idea what that is.
   */
  rowProps?: (row: T) => HTMLAttributes<HTMLTableRowElement>;
  emptyTitle?: string;
  emptyMessage?: string;
  emptyAction?: ReactNode;

  /* Server-side paging. The parent owns page and size; this only renders the controls. Client-side
   * filtering is deliberately not offered — with a thousand employees the rows for a page are all
   * that ever reach the browser, so there is nothing here to filter. */
  page?: number;
  pageSize?: number;
  totalElements?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

const SIZE_OPTIONS = [10, 25, 50, 100];

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  onRowClick,
  rowProps,
  emptyTitle = "Nothing here yet",
  emptyMessage,
  emptyAction,
  page = 0,
  pageSize = 25,
  totalElements = 0,
  onPageChange,
  onPageSizeChange,
}: DataTableProps<T>) {
  const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
  const showFooter = Boolean(onPageChange) && totalElements > 0;

  return (
    <div className="overflow-hidden rounded-xl border border-ink-200 bg-surface shadow-sm">
      {loading ? (
        <div className="grid place-items-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title={emptyTitle} message={emptyMessage} action={emptyAction} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-ink-100 bg-ink-50/60">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={cn("px-4 py-3 font-medium text-ink-500", column.className)}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  {...(rowProps ? rowProps(row) : {})}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    "border-b border-ink-100/70 last:border-0",
                    onRowClick && "cursor-pointer hover:bg-ink-50",
                  )}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn("px-4 py-3 text-ink-700", column.className)}
                    >
                      {column.render
                        ? column.render(row)
                        : String((row as Record<string, unknown>)[column.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && showFooter && (
        <div className="flex items-center justify-between gap-3 border-t border-ink-100 px-4 py-3 text-sm text-ink-500">
          <div className="flex items-center gap-3">
            <span className="tnum">
              {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalElements)} of{" "}
              {totalElements}
            </span>
            {onPageSizeChange && (
              <span className="flex items-center gap-1.5 text-xs">
                <span className="text-ink-400">Rows</span>
                <select
                  value={pageSize}
                  onChange={(event) => {
                    onPageSizeChange(Number(event.target.value));
                    onPageChange?.(0);
                  }}
                  className="rounded-md border border-ink-200 bg-surface px-1.5 py-1 text-xs text-ink-700 focus:outline-none"
                >
                  {SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous page"
              className="rounded-md p-1.5 transition-colors hover:bg-ink-100 disabled:opacity-40 disabled:hover:bg-transparent"
              onClick={() => onPageChange?.(Math.max(0, page - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="tnum px-2">
              {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              aria-label="Next page"
              className="rounded-md p-1.5 transition-colors hover:bg-ink-100 disabled:opacity-40 disabled:hover:bg-transparent"
              onClick={() => onPageChange?.(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
